import React, { useCallback, useEffect, useRef, useState } from 'react';
import FilesCard from '../components/jobs/FilesCard';
import { FileViewer, quoteOwner, useUploader } from '../components/jobs/files';
import { Alert, Box, Button, CircularProgress, IconButton, Link, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import AddTaskIcon from '@mui/icons-material/AddTask';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/layout/Layout';
import ConfirmDialog from '../components/common/ConfirmDialog';
import QuoteFormDialog from '../components/quotes/QuoteFormDialog';
import StatusFlow from '../components/common/StatusFlow';
import QuoteItemsCard from '../components/quotes/QuoteItemsCard';
import CreateJobsDialog from '../components/quotes/CreateJobsDialog';
import CategoryTags from '../components/quotes/CategoryTags';
import { CategoryTag, InfoRow, OwnerList, PaidBadge, SectionCard, StatusPill } from '../components/common/ui';
import QuoteService from '../services/QuoteService';
import { errorMessage } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { useMoneyLock } from '../contexts/MoneyContext';
import { QUOTE_STATUS, QUOTE_STATUS_ORDER } from '../styles/tokens';
import { fmtDate, fmtNumber } from '../utils/format';
import { usePageRefresh } from '../components/layout/pullToRefresh';

export default function QuoteDetailPage() {
    const { id } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [quote, setQuote] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [jobOpen, setJobOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const money = useMoneyLock(`quote:${id}`);

    const load = useCallback(() => {
        return QuoteService.get(id).then(setQuote).catch((err) => {
            if (err?.response?.status === 404) setNotFound(true);
            else showToast(t('common.loadError'), 'error');
        });
    }, [id, showToast, t, money.visible]); // eslint-disable-line react-hooks/exhaustive-deps -- reload when prices are shown/hidden
    usePageRefresh(load);
    useEffect(() => { setNotFound(false); load(); }, [load]);
    useEffect(() => { setQuote(null); }, [id]);

    // A quote without positions has nothing to hide: it opens unlocked so positions can be added without the
    // password. Once it has positions, the password is needed the next time it is opened.
    const isEmpty = quote && String(quote.id) === String(id) && quote.items.length === 0;
    const triedEmpty = useRef(null);
    useEffect(() => {
        if (!isEmpty || money.visible || triedEmpty.current === id) return;
        triedEmpty.current = id;
        QuoteService.unlockEmpty(id).then(r => money.adopt(r.token)).catch(() => {});
    }, [isEmpty, money, id]);

    // PDFs and photos on the quote (same components as on jobs).
    const owner = quoteOwner(id);
    const [files, setFiles] = useState([]);
    const [viewer, setViewer] = useState(null);
    const [fileToDelete, setFileToDelete] = useState(null);
    const loadFiles = useCallback(() => { QuoteService.files(id).then(setFiles).catch(() => {}); }, [id]);
    useEffect(() => { setFiles([]); loadFiles(); }, [loadFiles]);
    const { upload, progress } = useUploader(owner, loadFiles);
    const deleteFile = async () => {
        try {
            await QuoteService.deleteFile(id, fileToDelete.id);
            showToast(t('files.deleted'), 'success');
            setViewer(null);
            loadFiles();
        } catch (err) {
            showToast(errorMessage(err, t('common.error')), 'error');
        } finally {
            setFileToDelete(null);
        }
    };

    // Confirmation, toast and Undo are handled by StatusFlow; errors propagate to it.
    const changeStatus = async (status) => {
        const updated = await QuoteService.setStatus(quote.id, status);
        setQuote(updated);
        // Accepting is usually followed by starting the work.
        if (status === 'ACCEPTED' && updated.items.some(i => !i.job)) setJobOpen(true);
    };

    const remove = async () => {
        try {
            await QuoteService.remove(quote.id);
            showToast(t('quotes.toast.deleted'), 'success');
            navigate('/quotes', { replace: true });
        } catch (err) {
            showToast(errorMessage(err, t('common.error')), 'error');
            setDeleteOpen(false);
        }
    };

    if (notFound) {
        return (
            <Layout>
                <Box sx={{ textAlign: 'center', mt: 10 }}>
                    <Typography variant="h6" fontWeight={700}>{t('quotes.notFound')}</Typography>
                    <Button sx={{ mt: 2 }} onClick={() => navigate('/quotes')}>{t('quotes.backToQuotes')}</Button>
                </Box>
            </Layout>
        );
    }
    if (!quote) return <Layout><Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box></Layout>;

    // Once jobs exist the quote header and status are frozen; positions not yet in a job stay editable.
    const locked = quote.jobs.length > 0;
    const openCount = quote.items.filter(i => !i.job).length;
    const canCreateJobs = quote.status === 'ACCEPTED' && openCount > 0;
    const quoteLabel = (s) => t(`quotes.status.${s}`);
    const nextSteps = {
        DRAFT: [{ to: 'SENT', label: t('quotes.flow.markSent'), icon: <SendOutlinedIcon /> }],
        SENT: [
            { to: 'ACCEPTED', label: t('quotes.flow.markAccepted'), color: 'success', icon: <CheckIcon /> },
            { to: 'REJECTED', label: t('quotes.flow.markRejected'), color: 'error', variant: 'outlined', icon: <CloseIcon /> },
        ],
    }[quote.status] || [];
    const confirmOf = (to) => {
        const forward = QUOTE_STATUS_ORDER.indexOf(to) > QUOTE_STATUS_ORDER.indexOf(quote.status) || (quote.status === 'ACCEPTED' && to === 'REJECTED');
        const vars = { number: quote.number, amount: money.fmt(quote.price), from: quoteLabel(quote.status), to: quoteLabel(to), interpolation: { escapeValue: false } };
        return {
            title: t(`quotes.flow.confirm.${to}.title`, vars),
            text: forward ? t(`quotes.flow.confirm.${to}.text`, vars) : t('status.backText', vars),
            confirmLabel: t(`quotes.flow.confirm.${to}.button`),
        };
    };

    return (
        <Layout>
            <Box sx={{ mb: 3 }}>
                <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/quotes')} sx={{ color: '#64748B', mb: 1, ml: -1 }}>{t('quotes.title')}</Button>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#94A3B8', letterSpacing: 0.4 }}>{quote.number}</Typography>
                            <CategoryTags categories={quote.categories} />
                        </Box>
                        <Typography variant="h5" fontWeight={700} sx={{ color: '#1a2332', letterSpacing: -0.3, wordBreak: 'break-word' }}>{quote.title}</Typography>
                        <Link component={RouterLink} to={`/clients/${quote.client.id}`} underline="hover" sx={{ fontSize: 14, color: '#427898', fontWeight: 600 }}>{quote.client.name}</Link>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        {/* Prices need the password every time; leaving the quote hides them again. */}
                        {isEmpty ? null : money.visible
                            ? <Button startIcon={<VisibilityOffOutlinedIcon />} onClick={money.hide} sx={{ color: '#64748B' }}>{t('money.hidePrices')}</Button>
                            : <Button variant="outlined" startIcon={<VisibilityOutlinedIcon />} onClick={money.show}>{t('money.showPrices')}</Button>}
                        <Button variant="outlined" startIcon={<PrintOutlinedIcon />} onClick={() => navigate(`/quotes/${quote.id}/print`)}>{t('quotes.print')}</Button>
                        {!locked && <Button variant="outlined" startIcon={<EditOutlinedIcon />} onClick={() => setEditOpen(true)}>{t('common.edit')}</Button>}
                        {!locked && (
                            <>
                                <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ border: '1px solid #E2E8F0', borderRadius: '10px' }} aria-label={t('common.more')}>
                                    <MoreHorizIcon />
                                </IconButton>
                                <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}
                                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                                    <MenuItem onClick={() => { setMenuAnchor(null); setDeleteOpen(true); }} sx={{ color: '#DC2626', fontSize: 14 }}>
                                        <ListItemIcon sx={{ color: '#DC2626' }}><DeleteOutlineIcon fontSize="small" /></ListItemIcon>{t('quotes.delete')}
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Status strip + the follow-up action */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between', bgcolor: '#fff', border: '1px solid #E2E8F0', borderRadius: '14px', p: 1.5, pl: 2, mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.8 }}>{t('jobs.fields.status')}</Typography>
                    <StatusFlow status={quote.status} order={QUOTE_STATUS_ORDER} styles={QUOTE_STATUS} labelOf={quoteLabel}
                                nextSteps={nextSteps} confirmOf={confirmOf} onChange={changeStatus}
                                successOf={(s) => t('quotes.toast.status', { status: quoteLabel(s) })}
                                locked={locked} lockedHint={t('quotes.flow.lockedHint')} />
                </Box>
                {canCreateJobs && (
                    <Button variant="contained" color="success" disableElevation startIcon={<AddTaskIcon />} onClick={() => setJobOpen(true)}>
                        {locked ? t('quotes.jobs.createRemaining', { count: openCount }) : t('quotes.jobs.create')}
                    </Button>
                )}
            </Box>

            {canCreateJobs && !locked && (
                <Alert severity="success" sx={{ mb: 2.5, borderRadius: '12px' }}>{t('quotes.acceptedHint')}</Alert>
            )}

            <Grid container spacing={2.5}>
                <Grid size={{ xs: 12, lg: 4 }}>
                    <SectionCard title={t('jobs.sections.details')}>
                        <Box sx={{ px: 2.5, py: 1 }}>
                            <InfoRow label={t('quotes.items.grandTotal')}>
                                <Box component="span" sx={{ fontSize: 18, fontWeight: 800 }}>{money.fmt(quote.total)}</Box>
                                <Box component="span" sx={{ fontSize: 12.5, color: '#64748B' }}> · {quote.vatEnabled ? t('vat.inclVat', { rate: fmtNumber(quote.vatRate) }) : t('vat.noVat')}</Box>
                            </InfoRow>
                            <InfoRow label={t('jobs.fields.client')}>
                                <Link component={RouterLink} to={`/clients/${quote.client.id}`} underline="hover" sx={{ color: '#427898', fontWeight: 600 }}>{quote.client.name}</Link>
                            </InfoRow>
                            <InfoRow label={t('quotes.fields.categories')}><CategoryTags categories={quote.categories} /></InfoRow>
                            {quote.ownerName && <InfoRow label={t('jobs.fields.ownerName')}>{quote.ownerName}</InfoRow>}
                            {quote.coOwners?.length > 0 && <InfoRow label={t('jobs.fields.coOwners')}><OwnerList names={quote.coOwners} /></InfoRow>}
                            {quote.municipality && <InfoRow label={t('jobs.fields.municipality')}>{quote.municipality}</InfoRow>}
                            {quote.cadastralZone && <InfoRow label={t('jobs.fields.cadastralZone')}>{quote.cadastralZone}</InfoRow>}
                            {quote.propertySheet && <InfoRow label={t('jobs.fields.propertySheet')}>{quote.propertySheet}</InfoRow>}
                            {quote.parcelNumber && <InfoRow label={t('jobs.fields.parcelNumber')}>{quote.parcelNumber}</InfoRow>}
                            <InfoRow label={t('jobs.fields.created')}>{fmtDate(quote.createdAt)}</InfoRow>
                            {quote.sentAt && <InfoRow label={t('quotes.fields.sentAt')}>{fmtDate(quote.sentAt)}</InfoRow>}
                            {quote.decidedAt && <InfoRow label={t(`quotes.fields.decidedAt.${quote.status}`)}>{fmtDate(quote.decidedAt)}</InfoRow>}
                        </Box>
                    </SectionCard>
                </Grid>
                <Grid size={{ xs: 12, lg: 8 }} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {quote.jobs.length > 0 && (
                        <SectionCard title={t('quotes.jobs.title', { count: quote.jobs.length })}>
                            {quote.jobs.map(j => (
                                <Box key={j.id} component={RouterLink} to={`/jobs/${j.id}`} sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: 2.5, py: 1.5, textDecoration: 'none',
                                    borderBottom: '1px solid #F1F5F9', '&:last-of-type': { borderBottom: 0 }, '&:hover': { bgcolor: '#FAFBFC' },
                                }}>
                                    <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
                                        <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 700, color: '#0F172A' }}>{j.title}</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                                            <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>{j.number}</Typography>
                                            <CategoryTag category={j.category} />
                                        </Box>
                                    </Box>
                                    <StatusPill status={j.status} size="sm" />
                                    <Box sx={{ textAlign: 'right', minWidth: 90 }}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>{money.fmt(j.price)}</Typography>
                                        <PaidBadge paid={j.paid} />
                                    </Box>
                                </Box>
                            ))}
                        </SectionCard>
                    )}
                    <QuoteItemsCard quote={quote} money={money} editable onChange={setQuote} />
                    <FilesCard owner={owner} files={files} upload={upload} progress={progress}
                               openFile={(list, index) => setViewer({ list, index })} askDelete={setFileToDelete} />
                    {quote.description && (
                        <SectionCard title={t('quotes.fields.description')}>
                            <Typography sx={{ px: 2.5, py: 2, fontSize: 14, lineHeight: 1.6, color: '#334155', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {quote.description}
                            </Typography>
                        </SectionCard>
                    )}
                </Grid>
            </Grid>

            <QuoteFormDialog open={editOpen} quote={quote} onClose={() => setEditOpen(false)} onSaved={(q) => { setQuote(q); setEditOpen(false); }} />
            <CreateJobsDialog open={jobOpen} quote={quote} money={money} onClose={() => setJobOpen(false)}
                              onDone={(q) => { setQuote(q); setJobOpen(false); }} />
            <FileViewer owner={owner} files={viewer?.list || []} index={viewer?.index ?? null}
                        onIndex={(i) => setViewer(v => ({ ...v, index: i }))} onClose={() => setViewer(null)} onDelete={setFileToDelete} />
            <ConfirmDialog open={!!fileToDelete} title={t('files.deleteTitle')} text={t('files.deleteText', { name: fileToDelete?.name })}
                           onConfirm={deleteFile} onClose={() => setFileToDelete(null)} />
            <ConfirmDialog open={deleteOpen} title={t('quotes.deleteTitle', { number: quote.number })} text={t('quotes.deleteText')}
                           onConfirm={remove} onClose={() => setDeleteOpen(false)} />
        </Layout>
    );
}
